import { CopyButton } from "./CopyButton";
import {
  TrendingUp, TrendingDown, Target, Gauge, Star, AlertTriangle,
  Copy, Check, Coins, Clock, Send,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const SetupRow = ({ label, value, color, testId, copyId }) => (
  <div className="flex items-center justify-between rounded-xl border px-4 py-3"
    style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
    <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
    <div className="flex items-center gap-2">
      <span data-testid={testId} className="font-mono-term text-base sm:text-lg font-bold" style={{ color }}>
        {value ?? "—"}
      </span>
      <CopyButton value={value} label={label} testId={copyId} />
    </div>
  </div>
);

export default function AnalysisResult({ result, onTelegram, telegram }) {
  const [copiedSignal, setCopiedSignal] = useState(false);
  const isLong = result.direction === "LONG";
  const dirColor = isLong ? "var(--long)" : "var(--short)";
  const dirBg = isLong ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)";
  const dirBorder = isLong ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)";
  const glow = isLong ? "0 0 30px rgba(16,185,129,0.25)" : "0 0 30px rgba(239,68,68,0.25)";
  const s = result.setup;

  const copySignal = async () => {
    try {
      await navigator.clipboard.writeText(result.signal_text || "");
      setCopiedSignal(true);
      toast.success("Sinyal kopyalandı");
      setTimeout(() => setCopiedSignal(false), 1600);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  return (
    <div data-testid="analysis-result-card" className="fade-up rounded-2xl border overflow-hidden"
      style={{ background: "var(--bg-card)", borderColor: dirBorder, boxShadow: glow }}>

      {/* Header: coin / timeframe / direction */}
      <div className="px-5 sm:px-7 pt-6 pb-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span data-testid="result-coin" className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg border"
            style={{ borderColor: "var(--border-subtle)", color: "var(--text-primary)" }}>
            <Coins className="w-4 h-4 text-amber-500" /> {result.coin}
          </span>
          <span data-testid="result-timeframe" className="inline-flex items-center gap-1.5 text-sm font-mono-term px-3 py-1.5 rounded-lg border"
            style={{ borderColor: "var(--border-subtle)", color: "var(--text-secondary)" }}>
            <Clock className="w-3.5 h-3.5" /> {result.timeframe}
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md"
            style={{ background: "rgba(16,185,129,0.1)", color: "var(--long)" }}>
            İşleme eklendi
          </span>
        </div>

        <div data-testid="result-direction-badge"
          className="flex items-center gap-3 px-6 py-4 rounded-2xl border"
          style={{ background: dirBg, borderColor: dirBorder }}>
          {isLong ? <TrendingUp className="w-9 h-9" style={{ color: dirColor }} />
                  : <TrendingDown className="w-9 h-9" style={{ color: dirColor }} />}
          <span className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: dirColor }}>
            {isLong ? "🟢 LONG" : "🔴 SHORT"}
          </span>
        </div>
      </div>

      {/* confidence + reasons */}
      <div className="px-5 sm:px-7 py-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border px-5 py-5 flex items-center gap-4" style={{ borderColor: "var(--border-subtle)", background: "rgba(245,158,11,0.08)" }}>
          <Gauge className="w-8 h-8 text-amber-500" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Güven Derecesi</div>
            <div data-testid="result-confidence" className="font-mono-term text-4xl font-bold" style={{ color: "var(--conf)" }}>%{result.confidence}</div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{result.confidence_label}</div>
          </div>
        </div>
        <div className="rounded-2xl border px-4 py-4" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
          <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
            <Target className="w-3.5 h-3.5 text-amber-500" /> Teknik Gerekçeler
          </h4>
          <ol data-testid="result-reasons" className="space-y-1.5 text-sm">
            {(result.reasons || []).map((r, i) => (
              <li key={i} className="flex gap-2"><span className="font-bold" style={{ color: dirColor }}>{i + 1}.</span><span style={{ color: "var(--text-primary)" }}>{r}</span></li>
            ))}
            {(!result.reasons || result.reasons.length === 0) && <li style={{ color: "var(--text-muted)" }}>—</li>}
          </ol>
        </div>
      </div>

      {result.price_readable === false && (
        <div className="mx-5 sm:mx-7 mb-4 flex items-start gap-2 text-sm px-4 py-3 rounded-xl border"
          style={{ background: "rgba(245,158,11,0.1)", borderColor: "rgba(245,158,11,0.3)", color: "#FCD34D" }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Grafikte fiyat yeterince okunamadı. Giriş/TP/SL seviyeleri hesaplanamadı. Yön görsel yapıya göre verildi.
        </div>
      )}

      {/* trade setup */}
      {s && (
        <div className="px-5 sm:px-7 pb-5">
          <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>İşlem Kurulumu (Trade Setup)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SetupRow label="💰 Giriş 1 (Market)" value="Market" color="var(--entry)" testId="setup-entry1" copyId="copy-entry1" />
            <SetupRow label="🎯 Giriş Bölgesi" value={s.entry_zone} color="var(--entry)" testId="setup-entry-zone" copyId="copy-entry-zone" />
            <SetupRow label="🛑 Stop Loss" value={s.sl} color="var(--short)" testId="setup-sl" copyId="copy-sl" />
            <SetupRow label="⚖️ R/R" value={s.rr} color="var(--rr)" testId="setup-rr" copyId="copy-rr" />
            <SetupRow label="🎯 TP1 (Muhafazakar)" value={s.tp1} color="var(--long)" testId="setup-tp1" copyId="copy-tp1" />
            <SetupRow label="🎯 TP2 (Hedef)" value={s.tp2} color="var(--long)" testId="setup-tp2" copyId="copy-tp2" />
            <SetupRow label="🏆 Basket TP" value={s.tp3} color="var(--long)" testId="setup-tp3" copyId="copy-tp3" />
            <SetupRow label="⚡ Kaldıraç" value="10x" color="var(--conf)" testId="setup-leverage" copyId="copy-leverage" />
          </div>

          <div className="mt-3 rounded-xl border px-4 py-3 text-sm flex items-center gap-2"
            style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.06)", color: "#FCD34D" }}>
            <Star className="w-4 h-4 shrink-0" />
            Kaldıraç <span className="font-bold">10x</span> · Kasanın <span className="font-bold">%1.5</span>'i ile işleme giriş yapın.
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button
              data-testid="copy-signal-button"
              onClick={copySignal}
              className="flex-1 rounded-xl border px-4 py-3.5 inline-flex items-center justify-center gap-2 font-semibold text-sm transition-all hover:brightness-110 text-white"
              style={{ background: dirColor, borderColor: dirBorder }}
            >
              {copiedSignal ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} KOPYALANABİLİR SİNYALİ KOPYALA
            </button>
            {telegram?.is_admin && (
              <button
                data-testid="telegram-send-button"
                onClick={() => onTelegram?.(result.id)}
                title={telegram?.configured ? "Telegram kanalına gönder" : "Telegram henüz yapılandırılmadı"}
                className="rounded-xl border px-4 py-3.5 inline-flex items-center justify-center gap-2 font-semibold text-sm transition-all hover:brightness-110"
                style={{ background: "rgba(59,130,246,0.12)", borderColor: "rgba(59,130,246,0.4)", color: "#60A5FA" }}
              >
                <Send className="w-4 h-4" /> Telegram'a Gönder
              </button>
            )}
          </div>
        </div>
      )}

      {/* disclaimer */}
      <div className="px-5 sm:px-7 pb-6">
        <div className="rounded-xl border px-4 py-4 flex items-start gap-3"
          style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.06)" }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <div className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            <span className="font-bold" style={{ color: "#FCD34D" }}>Sorumluluk Reddi: </span>
            {result.risk_note} Bu analiz <span className="font-semibold">yatırım tavsiyesi değildir</span>. Lütfen her zaman
            <span className="font-semibold" style={{ color: "var(--text-primary)" }}> düşük kaldıraç</span> kullanın ve işlemlerinizi mutlaka
            <span className="font-semibold" style={{ color: "#FCA5A5" }}> Stop Loss (SL)</span> ile açın. Tüm sorumluluk size aittir.
          </div>
        </div>
      </div>
    </div>
  );
}
